import React, { useState, useRef, useEffect, useContext } from "react";
import { useTranslation } from 'react-i18next';
import { useNavigate } from "react-router-dom";
import "./NavBar.css";
import Notification from "./Notifications"; // Ensure this component exists
import AuthContext from "../../contexts/AuthProvider";

import { API_URL } from "../../utils/config";
const baseUrl = API_URL;

const Navbar = ({ setMenuToggle }) => {
  const { t } = useTranslation("navbar");
  const navigate = useNavigate();
  const [activeDropdown, setActiveDropdown] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [showNotification, setShowNotification] = useState(false); // state for notifications dropdown
  const [hasNewNotification, setHasNewNotification] = useState(false); // flag to indicate unread notifications
  const [unreadCount, setUnreadCount] = useState(0); // count of unread notifications
  const [notifications, setNotifications] = useState([]);
  const actionsDropdownRef = useRef(null);
  const notificationDropdownRef = useRef(null);
  const prevNotificationCountRef = useRef(0);
  const { logout, isAuthenticated } = useContext(AuthContext);
  const getLangFromLocalStorage = () => {
    return localStorage.getItem("language") || "en";
  };

  // POST /api/v1/attnd-venue/auth/refresh
  const refreshToken = async () => {
    try {
      const response = await fetch(`${baseUrl}/api/v1/attnd-venue/auth/refresh`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          lang: getLangFromLocalStorage(),
        },
      });

      if (!response.ok) {
        throw new Error("Failed to refresh token");
      }

      const data = await response.json();
      console.log("Token refreshed successfully:", data);
      return true;
      // Optionally update any state or context with the new token info
    } catch (error) {
      console.error("Error refreshing token:", error);
      logout();
      navigate("/login");
      return false;
    }
  };

  useEffect(() => {
    const fetchProfile = async () => {
      if (!isAuthenticated) return;
      
      try {
        const response = await fetch(`${baseUrl}/api/v1/attnd-venue/auth/view-profile`, {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            lang: getLangFromLocalStorage(),
          },
        });
        
        if (!response.ok) {
          // if 401 or 403 unauthorized or forbidden, refresh token
          if (response.status === 401 || response.status === 403) {
            const refreshSuccess = await refreshToken();
            if (refreshSuccess) {
              // Retry the original request after successful token refresh
              const retryResponse = await fetch(`${baseUrl}/api/v1/attnd-venue/auth/view-profile`, {
                method: "GET",
                credentials: "include",
                headers: {
                  "Content-Type": "application/json",
                  lang: getLangFromLocalStorage(),
                },
              });
              if (retryResponse.ok) {
                const retryData = await retryResponse.json();
                setUserProfile(retryData);
                localStorage.setItem("user", JSON.stringify(retryData.data));
                return;
              }
            }
          }
          throw new Error("Network response was not ok");
        }
        
        const data = await response.json();
        setUserProfile(data);
        localStorage.setItem("user", JSON.stringify(data.data));
      } catch (error) {
        console.error("Error fetching profile:", error);
      }
    };
    
    fetchProfile();
  }, [isAuthenticated, logout, navigate]);

  // Listen for profile updates (e.g., image and name changes)
  useEffect(() => {
    const handleProfileUpdated = (event) => {
      setUserProfile((prev) => {
        if (prev && prev.data) {
          return {
            ...prev,
            data: {
              ...prev.data,
              image: event.detail.image,
              firstName: event.detail.firstName,
            },
          };
        }
        return prev;
      });
    };

    window.addEventListener("profileUpdated", handleProfileUpdated);
    return () => {
      window.removeEventListener("profileUpdated", handleProfileUpdated);
    };
  }, []);

  const handleLogout = () => {
    setActiveDropdown(false);
    logout();
    navigate("/login");
  };

  const handleProfile = () => {
    navigate("/profile");
    setActiveDropdown(false);
  };

  const handleDropdown = () => {
    setActiveDropdown((prev) => !prev);
  };

  // Close both dropdowns when clicking outside them.
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (activeDropdown) {
        if (
          actionsDropdownRef.current &&
          !actionsDropdownRef.current.contains(event.target) &&
          !event.target.closest(".user")
        ) {
          setActiveDropdown(false);
        }
      }
      if (showNotification) {
        if (
          notificationDropdownRef.current &&
          !notificationDropdownRef.current.contains(event.target) &&
          !event.target.closest(".notification")
        ) {
          setShowNotification(false);
        }
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [activeDropdown, showNotification]);

  // Retrieve local user info (if available)
  const localUser = localStorage.getItem("user")
    ? JSON.parse(localStorage.getItem("user"))
    : null;

  const displayedImage =
    userProfile?.data?.image || localUser?.image || "/default-profile.svg";
  const displayedName =
    userProfile?.data?.firstName || localUser?.firstName || "Guest";

  // Handle notification icon click.
  const handleNotificationClick = () => {
    setShowNotification((prev) => {
      const newState = !prev;
      if (newState) {
        setHasNewNotification(false);
        setNotifications((prevNotifications) => {
          const newNotifs = prevNotifications.filter((n) => n.isNew);
          if (newNotifs.length > 0) {
            const firstNew = newNotifs[0];
            const remaining = prevNotifications.filter(
              (n) => n.id !== firstNew.id
            );
            return [firstNew, ...remaining];
          }
          return prevNotifications;
        });
      }
      return newState;
    });
  };

  // Poll for notifications every 30 seconds.
  useEffect(() => {
    const fetchNotificationsForNavbar = async () => {
      if (!isAuthenticated) return;
      try {
        const response = await fetch(`${baseUrl}/api/v1/notifications/venue`, {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            lang: getLangFromLocalStorage(),
          },
        });
        if (!response.ok) {
          console.error("Failed to fetch notifications");
          if (response.status === 401 || response.status === 403) {
            const refreshSuccess = await refreshToken();
            if (refreshSuccess) {
              // Retry the notifications request after successful token refresh
              const retryResponse = await fetch(`${baseUrl}/api/v1/notifications/venue`, {
                method: "GET",
                credentials: "include",
                headers: {
                  "Content-Type": "application/json",
                  lang: getLangFromLocalStorage(),
                },
              });
              if (retryResponse.ok) {
                const retryData = await retryResponse.json();
                const retryNotif = Array.isArray(retryData)
                  ? retryData
                  : retryData.notifications || retryData.data || [];
                
                const retryUnreadCount = retryNotif.filter((n) => n.is_read === 0).length;
                console.log("Unread notifications count (after retry):", retryUnreadCount);
                setUnreadCount(retryUnreadCount);
                setHasNewNotification(retryUnreadCount > 0);
                setNotifications(retryNotif);
                localStorage.setItem("previousNotificationCount", retryNotif.length);
                return;
              }
            }
          }
          return;
        }
        const data = await response.json();
        const notif = Array.isArray(data)
          ? data
          : data.notifications || data.data || [];
        
        // Get the previous notification count from localStorage
        const previousCount = localStorage.getItem("previousNotificationCount") || 0;
// if is_read =0 count as unread
        const unreadCount = notif.filter((n) => n.is_read === 0).length;
        console.log("Unread notifications count:", unreadCount);
        setUnreadCount(unreadCount); // Update unread count state
        if (
          unreadCount > 0 
        ) {
          setHasNewNotification(true);
        } else {
          setHasNewNotification(false);
        }
        // Check if the notification count has increased
        // if (!showNotification && notif.length > previousCount) {
        //   setHasNewNotification(true); // Set the icon as new
        // }
        
        // Update state and store the current notification count in localStorage
        setNotifications(notif);
        localStorage.setItem("previousNotificationCount", notif.length);
      } catch (error) {
        console.error("Error fetching notifications:", error);
      }
    };

    fetchNotificationsForNavbar();

    const interval = setInterval(fetchNotificationsForNavbar, 30000);
    return () => clearInterval(interval);
  }, [showNotification]);

  return (
    <nav className="navbar-navbar">
      <div style={{ display: "flex", gap: "12px" }}>
        <button
          type="button"
          onClick={() => setMenuToggle((prev) => !prev)}
          className="menu-toggle"
        >
          <img src="/menu.svg" alt={t('Navbar.menuAlt')} />
        </button>
        <div className="logo">
          <img src="/logo2.svg" alt="Logo" />
        </div>
      </div>
      <div className="Search-container">
        <input
          type="text"
          className="search-input"
          placeholder={t('Navbar.searchPlaceholder')}
        />
      </div>
      <div className="user-notification">
        <div className="notification" onClick={handleNotificationClick}>
          <span className="notification-span">
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0 20C0 8.95431 8.95431 0 20 0C31.0457 0 40 8.95431 40 20C40 31.0457 31.0457 40 20 40C8.95431 40 0 31.0457 0 20Z" fill="#F4F1FF"/>
              <path d="M20 18.5204C19.59 18.5204 19.25 18.1804 19.25 17.7704V14.4404C19.25 14.0304 19.59 13.6904 20 13.6904C20.41 13.6904 20.75 14.0304 20.75 14.4404V17.7704C20.75 18.1904 20.41 18.5204 20 18.5204Z" fill="#5007C5"/>
              <path d="M20.0208 28.3502C17.4408 28.3502 14.8708 27.9402 12.4208 27.1202C11.5108 26.8202 10.8208 26.1702 10.5208 25.3502C10.2208 24.5302 10.3208 23.5902 10.8108 22.7702L12.0808 20.6502C12.3608 20.1802 12.6108 19.3002 12.6108 18.7502V16.6502C12.6108 12.5602 15.9308 9.24023 20.0208 9.24023C24.1108 9.24023 27.4308 12.5602 27.4308 16.6502V18.7502C27.4308 19.2902 27.6808 20.1802 27.9608 20.6502L29.2308 22.7702C29.7008 23.5502 29.7808 24.4802 29.4708 25.3302C29.1608 26.1802 28.4808 26.8302 27.6208 27.1202C25.1708 27.9502 22.6008 28.3502 20.0208 28.3502ZM20.0208 10.7502C16.7608 10.7502 14.1108 13.4002 14.1108 16.6602V18.7602C14.1108 19.5702 13.7908 20.7402 13.3708 21.4302L12.1008 23.5602C11.8408 23.9902 11.7808 24.4502 11.9308 24.8502C12.0808 25.2502 12.4208 25.5502 12.9008 25.7102C17.5008 27.2402 22.5608 27.2402 27.1608 25.7102C27.5908 25.5702 27.9208 25.2502 28.0708 24.8302C28.2308 24.4102 28.1808 23.9502 27.9508 23.5602L26.6808 21.4402C26.2608 20.7502 25.9408 19.5802 25.9408 18.7702V16.6702C25.9308 13.4002 23.2808 10.7502 20.0208 10.7502Z" fill="#5007C5"/>
              <path d="M19.9999 30.9003C18.9299 30.9003 17.8799 30.4603 17.1199 29.7003C16.3599 28.9403 15.9199 27.8903 15.9199 26.8203H17.4199C17.4199 27.5003 17.6999 28.1603 18.1799 28.6403C18.6599 29.1203 19.3199 29.4003 19.9999 29.4003C21.4199 29.4003 22.5799 28.2403 22.5799 26.8203H24.0799C24.0799 29.0703 22.2499 30.9003 19.9999 30.9003Z" fill="#5007C5"/>
              {unreadCount > 0 && (
                <>
                  <circle cx="30" cy="16" r="7" fill="#5007C5"/>
                  <text x="30" y="20" textAnchor="middle" fill="#FCFCFC" fontSize="10" fontFamily="Arial, sans-serif" fontWeight="600">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </text>
                </>
              )}
            </svg>

            {/* <img
              src={
                hasNewNotification
                  ? "/notifications.svg" // Show new icon if there are new notifications
                  : "/notificationsread.svg" // Default icon if no new notifications
              }
              alt={
                hasNewNotification
                  ? t('Navbar.notificationsAltNew')
                  : t('Navbar.notificationsAltRead')
              }
            /> */}
          </span>
        </div>
        {showNotification && (
          <div
            className="notifications-dropdown"
            ref={notificationDropdownRef}
          >
            <Notification notifications={notifications} />
          </div>
        )}
        <div className="User-Img_profile">
          <span className="notification-span2">
            <img
              className="user-img-nav"
              src={displayedImage}
              alt="User Profile"
            />
          </span>
        </div>
        <button className="user" onClick={handleDropdown}>
          <h1>{displayedName}</h1>
          <img className="logout" src="/arrow-down.svg" alt={t('Navbar.menuAlt')} />
        </button>
        {activeDropdown && (
          <ul className="navbar-dropdown" ref={actionsDropdownRef}>
            <li onClick={handleProfile}>
              <button type="button">{t('Navbar.profile')}</button>
            </li>
            <li onClick={handleLogout}>
              <button type="button">{t('Navbar.logout')}</button>
            </li>
          </ul>
        )}
      </div>
    </nav>
  );
};

export default Navbar;